import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Building2, Mail, Lock, User, Phone, MapPin, FileText } from "lucide-react";

const CustomerSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    confirm_password: "",
    company_name: "",
    phone: "",
    address: "",
    vat_number: ""
  });

  const [passwordStrength, setPasswordStrength] = useState("");

  const checkPasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) strength++;

    if (strength <= 2) return "Weak";
    if (strength <= 4) return "Medium";
    return "Strong";
  };

  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setFormData({ ...formData, password: newPassword });
    setPasswordStrength(checkPasswordStrength(newPassword));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirm_password) {
      toast.error("Passwords do not match");
      return;
    }

    if (passwordStrength === "Weak") {
      toast.error("Please use a stronger password");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/signup`, {
        ...formData,
        role: "customer"
      });

      if (response.status === 202) {
        toast.success("Account created! Awaiting admin approval.");
        navigate("/pending-approval");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Signup failed";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gold-50 to-red-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="text-center mb-8">
          <img
            src="https://i.postimg.cc/ZKyhXXBf/Screenshot-2024-11-03-at-12-42-58.png"
            alt="PlayBack Token"
            className="h-16 mx-auto mb-4 cursor-pointer"
            onClick={() => navigate("/")}
          />
          <h1 className="text-3xl font-bold text-gray-800">Company Registration</h1>
          <p className="text-gray-600 mt-2">Purchase volunteer hour tokens for ESG compliance</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Create Company Account</CardTitle>
            <CardDescription>
              Register your company to browse and purchase verified volunteer hour tokens
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Company Information */}
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Company Information
                </h3>
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    required
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    placeholder="e.g., Acme Corporation"
                  />
                </div>
                <div>
                  <Label htmlFor="vat_number">VAT Number</Label>
                  <Input
                    id="vat_number"
                    value={formData.vat_number}
                    onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                    placeholder="e.g., IE1234567X"
                  />
                </div>
                <div>
                  <Label htmlFor="address">Company Address *</Label>
                  <Input
                    id="address"
                    required
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="e.g., 123 Main Street, Dublin"
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div className="space-y-4 p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-green-900 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Contact Person
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      required
                      value={formData.first_name}
                      onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                      placeholder="John"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      required
                      value={formData.last_name}
                      onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                      placeholder="Doe"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john.doe@company.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+353 1 234 5678"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-4">
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handlePasswordChange}
                    placeholder="Enter password"
                  />
                  {formData.password && (
                    <p className={`text-xs mt-1 ${
                      passwordStrength === "Strong" ? "text-green-600" :
                      passwordStrength === "Medium" ? "text-yellow-600" :
                      "text-red-600"
                    }`}>
                      Password strength: {passwordStrength}
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">
                    Must contain 8+ characters, uppercase, lowercase, number, and special character
                  </p>
                </div>
                <div>
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    required
                    value={formData.confirm_password}
                    onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
                    placeholder="Confirm password"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="space-y-4">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full !bg-gold-600 hover:!bg-gold-700 !text-white"
                >
                  {loading ? "Creating Account..." : "Create Account"}
                </Button>
                <p className="text-center text-sm text-gray-600">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => navigate("/login")}
                    className="text-gold-600 hover:underline font-semibold"
                  >
                    Login here
                  </button>
                </p>
                <p className="text-center text-xs text-gray-500">
                  Your account will be reviewed and approved by our admin team
                </p>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CustomerSignup;
